<template>
  <div>
    <v-app-bar
      app
      flat
      outlined
      class="appbar"
    >
      <v-app-bar-nav-icon @click="$router.push('/')">
        <LawLabIcon class="app-icon ml-1" />
      </v-app-bar-nav-icon>

      <v-toolbar-title class="pl-0">
        <router-link
          to="/"
          class="font-weight-bold title-bar d-none d-md-inline"
        >
          LAWLAB
        </router-link>
        <span class="text-caption disabled ml-2">v{{ appTag }}-{{ appHash }}</span>
      </v-toolbar-title>

      <v-spacer />

      <v-btn
        v-if="canGlobalSearch"
        class="ml-4 d-none d-sm-flex justify-space-between text-none font-weight-regular px-3"
        height="36"
        outlined
        rounded
        style="max-width: 280px; width: 100%;"
        :title="`Search (${shortcutHint})`"
        @click="_ => openSearch()"
      >
        <span class="d-flex align-center text--disabled">
          <v-icon
            left
            x-small
          >search</v-icon>
          <span class="text-body-2">Global Search</span>
        </span>
        <span class="text-caption text--disabled font-weight-medium search-shortcut-hint">{{ shortcutHint }}</span>
      </v-btn>

      <v-btn
        v-if="canGlobalSearch"
        icon
        class="d-flex d-sm-none"
        title="Search"
        @click="_ => openSearch()"
      >
        <v-icon>search</v-icon>
      </v-btn>

      <v-divider
        vertical
        class="ml-7 mr-3"
      />

      <TmetricBox />

      <NotificationsBox />

      <v-btn
        v-if="canAccessSettings"
        icon
        title="Open Admin Menu"
        @click="_ => goToSettings()"
      >
        <v-icon>settings</v-icon>
      </v-btn>

      <CalendarSyncStatusButton v-if="canCalendarSync" />

      <v-divider
        vertical
        class="mx-5"
      />

      <v-menu
        transition="scroll-y-reverse-transition"
        offset-y
        :close-on-content-click="false"
      >
        <template #activator="{ on }">
          <v-btn
            icon
            class="mr-2"
            v-on="on"
          >
            <v-badge
              bordered
              bottom
              color="light-green darken-1"
              dot
              offset-x="10"
              offset-y="10"
            >
              <v-avatar
                color="secondary"
                size="40"
              >
                <span class="white--text">{{ avatar }}</span>
              </v-avatar>
            </v-badge>
          </v-btn>
        </template>

        <v-list>
          <v-list>
            <v-list-item>
              <v-list-item-content class="py-0">
                <v-list-item-title class="font-weight-bold text-subtitle-1">
                  {{ currentUser.display_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span class="text-caption">
                    {{ currentUser.login }} ({{ currentPlatformRoleName }})
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-divider />
          <v-list-item @click="_ => toggleDarkMode()">
            <v-list-item-action>
              <v-icon>{{ buttonDarkModeText[0] }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Use {{ buttonDarkModeText[1] }} Mode</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item :to="{ name: 'user-account' }">
            <v-list-item-action><v-icon>account_circle</v-icon></v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>User Account</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            href="https://innovationlawlab.org/docs/"
            target="_blank"
          >
            <v-list-item-action>
              <v-icon>help</v-icon>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title>Documentation</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            href="https://innovationlawlab.org/technical-support/"
            target="_blank"
          >
            <v-list-item-action>
              <v-icon>question_answer</v-icon>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title>Technical Support</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider />
          <v-list-item @click="_ => signOut()">
            <v-list-item-action>
              <v-icon>exit_to_app</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Sign Out</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-dialog
      v-if="canGlobalSearch"
      v-model="search.dialogOpen"
      max-width="600"
    >
      <v-card>
        <v-form
          accept-charset="UTF-8"
          action="/search"
          method="get"
          @submit="e => checkSearchForm(e)"
        >
          <v-text-field
            ref="searchInput"
            v-model="search.term"
            autofocus
            class="pa-4"
            :error-messages="searchErrorMessages"
            flat
            hide-details="auto"
            label="Global Search"
            name="term"
            prepend-inner-icon="search"
            single-line
            solo
            text
            @input="search.dirty = true"
          />
        </v-form>

        <template v-if="recentSearches.length">
          <v-divider />
          <div class="text-overline font-weight-bold primary--text pl-4 pt-3 pb-1">
            Recent
          </div>
          <v-list dense>
            <v-list-item
              v-for="term of recentSearches"
              :key="term"
              class="recent-search-item d-flex align-center"
              @click="_ => selectRecentSearch(term)"
            >
              <v-list-item-icon class="my-0 align-self-center">
                <v-icon small>
                  history
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content class="py-0">
                <v-list-item-title>{{ term }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="my-0 align-self-center">
                <v-btn
                  icon
                  small
                  title="Remove from recent searches"
                  @click.stop="_ => removeRecentSearch(term)"
                >
                  <v-icon small>
                    close
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'

import actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'
import { dateTimeDisplayFormat } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import { USER_NAMESPACE } from '@/store/user'

import CalendarSyncStatusButton from '@/components/Calendar/CalendarSyncStatusButton.vue'
import LawLabIcon from '@/components/Icons/LawLabIcon.vue'
import NotificationsBox from '@/components/Nav/NotificationsBox.vue'
import TmetricBox from '@/components/Nav/TmetricBox.vue'

const RECENT_SEARCHES_KEY = 'recent_searches'
const RECENT_SEARCHES_MAX = 5

export default {
  name: 'NavBar',

  components: {
    CalendarSyncStatusButton,
    LawLabIcon,
    NotificationsBox,
    TmetricBox,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      appHash: process.env.VUE_APP_GIT_HASH,
      appTag: process.env.VUE_APP_GIT_TAG,
      recentSearches: [],
      search: {
        dialogOpen: false,
        dirty: false,
        term: '',
      },
    }
  },

  computed: {
    ...mapFields([
      'currentUser',
    ]),

    avatar () {
      return (
        (this.currentUser.first_name && this.currentUser.first_name.length ? this.currentUser.first_name[0] : '') +
        (this.currentUser.last_name && this.currentUser.last_name ? this.currentUser.last_name[0] : '')
      )
    },

    buttonDarkModeText () {
      return !this.$vuetify.theme.dark ? ['mdi-weather-night', 'Dark'] : ['mdi-brightness-5', 'Light']
    },

    canAccessSettings () {
      return this.can(actions.Platform.Management.AccessSiteSettings)
    },

    canCalendarSync () {
      return this.can(actions.Platform.Calendar.SyncCalendar)
    },

    canGlobalSearch () {
      return this.can(actions.Platform.Tools.GlobalSearch) && !this.currentUser.isExternal
    },

    currentPlatformRoleName () {
      return this.currentUser?.platform_role?.name || ''
    },

    searchErrorMessages () {
      if (!this.search.dirty) return []

      const term = this.search.term ? this.search.term.trim() : ''
      if (!term) return ['A search term is required']

      const minLengthResult = rules.searchTermMin(this.search.term)
      return minLengthResult === true ? [] : [minLengthResult]
    },

    shortcutHint () {
      return /Mac|iPod|iPhone|iPad/.test(window.navigator.platform) ? '⌘K' : 'Ctrl+K'
    },

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  mounted () {
    const theme = localStorage.getItem('dark_theme')
    if (theme) {
      if (theme === 'true') this.$vuetify.theme.dark = true
      else this.$vuetify.theme.dark = false
    } else if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      this.$vuetify.theme.dark = true
      localStorage.setItem(
        'dark_theme',
        this.$vuetify.theme.dark.toString()
      )
    }

    window.addEventListener('keydown', this.handleGlobalKeydown)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },

  methods: {
    checkSearchForm (e) {
      this.search.dirty = true

      if (this.searchErrorMessages.length) {
        e.preventDefault()
        return
      }

      this.saveRecentSearch(this.search.term)

      if (this.$route.path === '/search') {
        // change URL to reflect new search, which triggers watcher in Search.vue,
        // without re-loading the page
        if (this.search.term !== this.$route.query.term) this.$router.push('/search?term=' + this.search.term)

        e.preventDefault()
      }

      this.closeSearch()
    },

    closeSearch () {
      this.search.dialogOpen = false
    },

    dateTimeDisplayFormat,

    goToSettings () {
      if (this.$route.name !== 'settings') this.$router.push({ name: 'settings' })
    },

    goToUserAccount () {
      if (this.$route.name !== 'user-account') this.$router.push({ name: 'user-account' })
    },

    handleGlobalKeydown (e) {
      if (!this.canGlobalSearch) return

      const key = e.key ? e.key.toLowerCase() : ''
      if ((e.metaKey || e.ctrlKey) && key === 'k') {
        e.preventDefault()
        this.openSearch()
      }
    },

    loadRecentSearches () {
      try {
        this.recentSearches = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY)) || []
      } catch {
        this.recentSearches = []
      }
    },

    async openSearch () {
      this.search.dialogOpen = true
      this.search.dirty = false
      this.search.term = ''
      this.loadRecentSearches()
      await this.$nextTick()
      if (this.$refs.searchInput) this.$refs.searchInput.focus()
    },

    removeRecentSearch (term) {
      this.recentSearches = this.recentSearches.filter(existing => existing !== term)
      window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(this.recentSearches))
    },

    saveRecentSearch (term) {
      const trimmed = term.trim()
      if (!trimmed) return

      this.recentSearches = [
        trimmed,
        ...this.recentSearches.filter(existing => existing.toLowerCase() !== trimmed.toLowerCase()),
      ].slice(0, RECENT_SEARCHES_MAX)

      window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(this.recentSearches))
    },

    selectRecentSearch (term) {
      this.search.term = term
      this.saveRecentSearch(term)

      if (this.$route.path === '/search') {
        if (term !== this.$route.query.term) this.$router.push('/search?term=' + term)
        this.closeSearch()
      } else {
        this.closeSearch()
        window.location.href = '/search?term=' + encodeURIComponent(term)
      }
    },

    signOut () {
      // because this log out is user-triggered,
      // remove saved page to not be returned to upon login
      this.handleSessionReturnTo({ returnTo: null })
      this.handleSessionInactive({ inactive: true })

      // to support logging out from all windows
      window.localStorage.setItem('logout', Date.now())
      window.localStorage.setItem('afteruserlogout', Date.now())
      this.logoutUserModel({ retainState: false }).then(_ => {
        if (this.$route.name !== 'login') window.location.reload(true)
      })
    },

    toggleDarkMode: function () {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark
      localStorage.setItem('dark_theme', this.$vuetify.theme.dark.toString())
    },

    toggleMenu () {
      this.menuOpened = !this.menuOpened
      this.$emit('toggle-app-drawer')
    },

    ...mapActions([
      'handleSessionInactive',
      'handleSessionReturnTo',
      'logoutUserModel',
    ]),

  },
}
</script>

<style scoped>
.search-shortcut-hint {
  border: 1px solid currentColor;
  border-radius: 4px;
  opacity: 0.6;
  padding: 0 4px;
}

.recent-search-item:hover {
  background-color: var(--v-primary-base);
}

.recent-search-item:hover .v-list-item__title,
.recent-search-item:hover .v-icon {
  color: #fff !important;
}
</style>
